import env from '@shared/env.js'

const langMapper = {
  de: 'de-DE',
  en: 'en-US',
  es: 'es-US',
  'de-de': 'de-DE',
  'en-us': 'en-US',
  'es-us': 'es-US',
}

export const wait = (milliseconds) => {
  const isPlaywright =
    typeof window !== 'undefined' && window.IS_PLAYWRIGHT === true
  const delay = isPlaywright ? 0 : milliseconds
  return new Promise((resolve) => setTimeout(resolve, delay))
}

export const normalizeLang = (lang) => {
  lang = String(lang)
  return langMapper[lang.toLowerCase()] || lang
}
export const getLangCodeByLanguage = (lang) => lang.split('-')[0]
export const getSupportedLangCodes = () =>
  env.VITE_SUPPORTED_LANG_CODES.split(',')

// dont use this directly, user state.getters.lang instead
export function getNavigatorLanguage() {
  const lang =
    (window.navigator.languages || []).find(
      (k) => k.length === 5 || k.length === 2,
    ) ||
    window.navigator.language ||
    window.navigator.userLanguage
  return normalizeLang(lang)
}

export const getDefaultLanguage = () => {
  const searchParams = new URLSearchParams(window.location.search)
  let searchParamLanguage = searchParams.get('language') || ''
  searchParamLanguage = langMapper[searchParamLanguage.toLowerCase()]
  const supportedLangCodes = getSupportedLangCodes()
  const navigatorLanguage = getNavigatorLanguage()
  const defaultLangCode = getLangCodeByLanguage(
    searchParamLanguage ?? navigatorLanguage,
  )

  return supportedLangCodes.includes(defaultLangCode)
    ? normalizeLang(defaultLangCode)
    : normalizeLang(supportedLangCodes[0])
}

export const arrayToObject = (fields = []) => {
  if (!Array.isArray(fields)) {
    throw new Error(
      `'fields' expected to be an array, ${
        fields === null ? 'null' : typeof fields
      } got instead`,
    )
  }
  return fields.reduce((prev, path) => {
    if (typeof path !== 'string') {
      path = String(path)
    }

    const key = path.split('.').slice(-1)[0]

    if (prev[key]) {
      throw new Error(`The key \`${key}\` is already in use.`)
    }

    prev[key] = path

    return prev
  }, {})
}

// Navigator.maxTouchPoints-based mobile detection taken from
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#Mobile_Device_Detection
export const supportsTouch = () => {
  if ('maxTouchPoints' in navigator) {
    return navigator.maxTouchPoints > 0
  }
  if ('msMaxTouchPoints' in navigator) {
    return navigator.msMaxTouchPoints > 0
  }
  const mQ = window.matchMedia && matchMedia('(pointer:coarse)')
  if (mQ && mQ.media === '(pointer:coarse)') {
    return !!mQ.matches
  }
  if ('orientation' in window) {
    return true // deprecated, but good fallback
  }
  // Only as a last resort, fall back to user agent sniffing
  const UA = navigator.userAgent
  return (
    /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
    /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
  )
}

export const likelyHasAppStore = () =>
  /iPhone|iPad|iPod|iOS/i.test(navigator.userAgent) ||
  (navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)) ||
  (navigator.userAgent.includes('Mac') && 'ontouchend' in document)

export const likelyHasPlayStore = () =>
  (/Android/i.test(navigator.userAgent) ||
    (navigator.platform && /Linux/.test(navigator.platform))) &&
  !/Kindle|Amazon|FireOS/i.test(navigator.userAgent)

export const getTimezone = () =>
  window.Intl && typeof window.Intl === 'object'
    ? window.Intl.DateTimeFormat().resolvedOptions().timeZone
    : env.VITE_DEFAULT_TIMEZONE

export const coerceAtLeast = (val, min) =>
  val === null || val === undefined || val < min ? min : val

export const isNumeric = (k) => /^[0-9]+$/.test(k)

const versionChunkCompare = (l, r) => {
  const lnumeric = isNumeric(l)
  const rnumeric = isNumeric(r)

  return l === r
    ? 0
    : lnumeric && !rnumeric
      ? -1
      : !lnumeric && rnumeric
        ? 1
        : Number(l) < Number(r)
          ? -1
          : 1
}

/**
 * Compares two semantic version strings and determines their order.
 *
 * @param {string} lver - The left-hand semantic version string to compare.
 * @param {string} rver - The right-hand semantic version string to compare.
 * @returns {number|undefined} - Returns:
 *   - `0` if the versions are equal,
 *   - `-1` if `lver` is less than `rver`,
 *   - `1` if `lver` is greater than `rver`,
 *   - `undefined` if either version string is not in valid semantic version format.
 */
export const semVerCompare = (lver, rver) => {
  const lSemVer = String(lver).trim().split('.')
  const rSemVer = String(rver).trim().split('.')
  if (lSemVer.length !== 3 || rSemVer.length !== 3) return undefined
  return (
    versionChunkCompare(lSemVer[0], rSemVer[0]) ||
    versionChunkCompare(lSemVer[1], rSemVer[1]) ||
    versionChunkCompare(lSemVer[2], rSemVer[2])
  )
}

// test whether a key contains all of the substrings provided in the arguments.
// case is ignored. no other sanity checks.
const keySearchGeneric = (key, ...args) =>
  typeof key === 'string' &&
  key.length > 0 &&
  args.every((term) => key.toLowerCase().includes(term.toLowerCase()))

// these functions are used to select prefill data from fields where the key is not known.
// not to be used for other purposes.
export const isEmailKey = (key) => keySearchGeneric(key, 'email')
export const isFirstNameKey = (key) => /first[^a-zA-Z]?(?:name)?$/i.test(key)
export const isLastNameKey = (key) => /last[^a-zA-Z]?(?:name)?$/i.test(key)
export const isDoBKey = (key) =>
  keySearchGeneric(key, 'dob') || keySearchGeneric(key, 'date', 'of', 'birth')

export const debounce = (func, timeout = 300) => {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
}

/**
 * URI decode the value of every query string component. Non-string values
 * (vue-router queries carry null and arrays) are coerced to strings.
 * @param {Object.<string, any>} query containing encoded values
 * @returns {Object.<string, string>} query containing decoded values
 */
export const decodeQuery = (query) =>
  Object.keys(query).reduce((result, key) => {
    result[key] = decodeURIComponent(query[key])
    return result
  }, {})

export const validateDate = (year, month, day) => {
  const d = new Date(year, month, day)
  if (d.getFullYear() == year && d.getMonth() == month && d.getDate() == day) {
    return true
  }
  return false
}

/**
 * generates a signupContexts object filled with all information that is known about the user at start time
 * of the webcheckout. This is to be used on all product variants.
 * - the referrer url will be empty if the document.referrer is the same as the current url
 * - the protocol (`http://` or `https://`) are not part of the payload so that they are not blocked by WAF
 * @returns {{webEntryPointUrl: string, webEntryPointReferrerUrl: (string)}} the signupContext
 */
export const generateSignupContext = () => {
  const referrerDifferentFromHref =
    document.referrer === window.location.href ? '' : document.referrer
  return {
    webEntryPointUrl: window.location.href.split('://').pop(),
    webEntryPointReferrerUrl: referrerDifferentFromHref.split('://').pop(),
  }
}

/**
 * sets the page title
 * @param newTitle
 */
export const setPageTitle = (newTitle) => {
  if (newTitle && typeof newTitle === 'string') {
    document.querySelector('title').textContent = newTitle
  }
}

/**
 * @param { Object } objectWithNullishEntries
 * @returns { {key: string, value: any}[] } containing all entries from the input data that are not nullish
 */
export const toListOfNonNullishKeyValueObjects = (objectWithNullishEntries) =>
  Object.entries(objectWithNullishEntries)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== '',
    )
    .map(([key, value]) => ({ key, value }))

/**
 * @param { Object } objectWithNullishEntries
 * @returns { Object } containing all entries from the input data that are not nullish
 */
export const toObjectOfNonNullishEntries = (objectWithNullishEntries) =>
  Object.fromEntries(
    Object.entries(objectWithNullishEntries).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  )

/**
 * Attempts to execute an operation and returns a tuple with the result or error.
 * If the operation is a Promise, it will resolve to a tuple.
 * If the operation is a synchronous function, it will return a tuple immediately.
 *
 * @template T - The type of the successful result.
 * @template E - The type of error that can be thrown.
 * @param {Promise<T> | (() => T)} operation - The operation to attempt.
 * @returns {Promise<[E, T]>} A tuple containing either the result or the error.
 */
export const attempt = async (operation) => {
  try {
    const data = await operation()
    return [data, null]
  } catch (error) {
    return [null, error]
  }
}

// Preserve the E2E deep-link capture function when one was installed before app startup.
window.location.redirectToDeeplink ??= (deeplink) =>
  window.location.assign(deeplink)
